import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import MarqueeBand from "@/components/MarqueeBand";
import Faq from "@/components/Faq";

export default function SpecializationPage({ testid, index, title, lines, subtitle, image, intro, benefits, specs, applications, accent, highlight, faqs, quoteService }) {
  return (
    <div data-testid={testid}>
      <PageHeader index={index} title={title} lines={lines} subtitle={subtitle} image={image} logo />

      {/* HIGHLIGHT — sezione in evidenza */}
      {highlight && (
        <section data-testid="spec-highlight" className="bg-primary text-primary-foreground">
          <div className="max-w-[1500px] mx-auto px-5 md:px-10 py-16 md:py-20 grid lg:grid-cols-12 gap-10 lg:gap-12 items-start">
            <div className="lg:col-span-5">
              <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest bg-primary-foreground/15 px-3 py-1.5 mb-6">
                <span className="w-2 h-2 bg-primary-foreground" /> {highlight.tag}
              </span>
              <h2 className="font-head font-black text-3xl md:text-5xl tracking-tightest leading-[0.95]">
                {highlight.title}
              </h2>
            </div>
            <div className="lg:col-span-7">
              <div className="space-y-4 text-primary-foreground/90 leading-relaxed text-lg">
                {highlight.body.map((p, i) => <p key={i}>{p}</p>)}
              </div>
              {highlight.steps && (
                <div className="mt-10 grid sm:grid-cols-3 gap-px bg-primary-foreground/20 border border-primary-foreground/20">
                  {highlight.steps.map((s, i) => (
                    <div key={i} className="bg-primary p-6">
                      <span className="font-mono text-3xl font-black">{String(i + 1).padStart(2, "0")}</span>
                      <h3 className="font-head font-bold text-lg tracking-tight mt-3 mb-2">{s.t}</h3>
                      <p className="text-primary-foreground/85 text-sm leading-relaxed">{s.d}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* INTRO + SPECS */}
      <section className="py-24 bg-background">
        <div className="max-w-[1500px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-widest text-primary mb-6">// Il materiale</p>
              <h2 className="font-head font-black text-3xl md:text-5xl tracking-tightest leading-[0.95] mb-6">{accent}</h2>
              <div className="space-y-5 text-muted-foreground leading-relaxed text-lg">
                {intro.map((p, i) => <p key={i}>{p}</p>)}
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-4 lg:col-start-9">
            <Reveal delay={0.15}>
              <div className="border border-border bg-card p-8">
                <p className="font-mono text-xs uppercase tracking-widest text-primary mb-6">Scheda Tecnica</p>
                <dl className="space-y-4">
                  {specs.map((s) => (
                    <div key={s.k} className="flex justify-between gap-4 border-b border-border pb-4 last:border-0 last:pb-0">
                      <dt className="font-mono text-xs uppercase tracking-widest text-muted-foreground">{s.k}</dt>
                      <dd className="font-mono text-sm font-medium text-right">{s.v}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <MarqueeBand />

      {/* BENEFITS */}
      <section className="py-24 bg-secondary/40 border-b border-border">
        <div className="max-w-[1500px] mx-auto px-5 md:px-10">
          <Reveal>
            <h2 className="font-head font-black text-3xl md:text-5xl tracking-tightest mb-14">Vantaggi e benefici</h2>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((b, i) => (
              <Reveal key={b.t} delay={(i % 3) * 0.08}>
                <div className="bg-card border border-border p-8 h-full">
                  <span className="font-mono text-4xl text-primary">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-head font-bold text-xl tracking-tight mt-4 mb-3">{b.t}</h3>
                  <p className="text-muted-foreground leading-relaxed">{b.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* APPLICATIONS */}
      <section className="py-24 bg-background">
        <div className="max-w-[1500px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Reveal>
              <h2 className="font-head font-black text-3xl md:text-4xl tracking-tightest sticky top-28">Dove si applica</h2>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <div className="grid sm:grid-cols-2 gap-x-10">
              {applications.map((a, i) => (
                <Reveal key={a} delay={(i % 2) * 0.06}>
                  <div className="flex items-center gap-4 py-5 border-b border-border">
                    <Check className="w-5 h-5 text-primary shrink-0" />
                    <span className="font-medium">{a}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      {faqs && <Faq items={faqs} />}

      {/* CTA */}
      <section className="py-24 bg-foreground text-background">
        <div className="max-w-[1500px] mx-auto px-5 md:px-10 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <h2 className="font-head font-black text-3xl md:text-5xl tracking-tightest max-w-xl leading-[0.95]">
            Vuoi valutare questo intervento sul tuo immobile?
          </h2>
          <Link to={quoteService ? `/preventivo?servizio=${encodeURIComponent(quoteService)}` : "/preventivo"} data-testid="spec-cta" className="shrink-0 inline-flex items-center gap-2 bg-primary text-primary-foreground font-mono text-sm uppercase tracking-widest px-8 py-5 hover:bg-background hover:text-foreground transition-colors">
            Richiedi Preventivo <ArrowUpRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
